import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import {
  useExtractAgriInputScheduleItemsMutation,
  useLazyGetTableCSVPresignedURLByTableTypeQuery,
} from '../../../services/mVPGraphQlService';
import { stageLabel } from '../utils/scheduleStage';
import { formatProductLabel } from '../hooks/useProductOptions';

const TEAL = 'rgba(0,105,112,1)';
const MAX_FILE_SIZE = 500 * 1024;

export type ImportedLine = {
  product: string;
  productLabel?: string;
  uplProduct?: boolean;
  stage: string;
  dasFrom: string;
  dasTo: string;
  mandatory: string;
};

type Header = {
  season?: string | null;
  region?: string | null;
  crop?: string | null;
  hybrid?: string | null;
};

type Args = {
  header: Header;
  onItems: (lines: ImportedLine[]) => void;
  onClear: () => void;
};

export const useScheduleFileImport = ({
  header,
  onItems,
  onClear,
}: Args) => {
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);

  const [getTemplateUrl, { isFetching: templateLoading }] =
    useLazyGetTableCSVPresignedURLByTableTypeQuery();
  const [getUploadUrl] =
    useLazyGetTableCSVPresignedURLByTableTypeQuery();
  const [extractItems] =
    useExtractAgriInputScheduleItemsMutation();

  const season = header.season?.trim() || '';
  const region = header.region?.trim() || '';
  const cropName = header.crop?.trim() || '';
  const hybridProductionCode = header.hybrid?.trim() || '';
  const canUpload =
    !!season && !!region && !!cropName && !!hybridProductionCode;

  const downloadTemplate = async () => {
    try {
      const response = (await getTemplateUrl({
        tableType: 'TT_AGRI_INPUT_SCHEDULE_ITEMS',
        csvFileType: 'TEMPLATE_FILE',
        wsId,
      } as any).unwrap()) as any;
      const url =
        response?.data?.GetTableCSVPresignedURLByTableType
          ?.templateUrl;
      if (!url) throw new Error('Template URL not found');
      window.open(url, '_blank');
    } catch (err) {
      console.error('Download template failed', err);
    }
  };

  const rejectFile = (message: string) => {
    setFile(null);
    onClear();
    setFileError(message);
  };

  const uploadAndExtract = async (selected: File) => {
    setUploading(true);
    try {
      const presigned = (await getUploadUrl({
        tableType: 'TT_AGRI_INPUT_SCHEDULE_ITEMS',
        count: 1,
        csvFileType: 'UPLOADED_FILE',
        wsId,
      } as any).unwrap()) as any;
      const { fileUrl, fileKey } =
        presigned?.data?.GetTableCSVPresignedURLByTableType
          ?.fileUploads?.[0] || {};
      if (!fileUrl || !fileKey) {
        rejectFile('Please try again.');
        return;
      }

      const upload = await fetch(fileUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'text/csv' },
        body: selected,
      });
      if (!upload.ok) {
        rejectFile('Please try again.');
        return;
      }

      const extracted = await extractItems({
        wsId,
        fileKey,
        season,
        region,
        cropName,
        hybridProductionCode,
      }).unwrap();
      const result =
        extracted?.data?.ExtractAgriInputScheduleItems;
      const status = result?.responseStatus;
      const failedRows = result?.errorCount ?? 0;

      if (status?.status !== 'SUCCESS') {
        rejectFile(
          status?.errorMessages?.[0] ||
            status?.reasons?.[0] ||
            'Please try again.'
        );
        return;
      }
      if (failedRows > 0) {
        const firstError = result?.errors?.[0];
        rejectFile(
          `${failedRows} ${
            failedRows === 1 ? 'row has' : 'rows have'
          } errors${
            firstError
              ? `. Row ${firstError.rowNumber}: ${firstError.message}`
              : '.'
          }`
        );
        return;
      }

      onItems(
        (result?.items ?? [])
          .filter((item) => !item.deleted)
          .map((item) => ({
            product:
              item.productId != null ? String(item.productId) : '',
            productLabel:
              formatProductLabel(item.product) ||
              (item.productId != null
                ? String(item.productId)
                : undefined),
            uplProduct: item.product?.uplProduct === true,
            stage: stageLabel(item.stage) || item.stageName || '',
            dasFrom:
              item.dasFrom != null ? String(item.dasFrom) : '',
            dasTo: item.dasTo != null ? String(item.dasTo) : '',
            mandatory: item.mandatory ? 'YES' : 'NO',
          }))
      );
    } catch (err) {
      console.error('Schedule file upload failed', err);
      rejectFile('Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const selectFile = (selected: File) => {
    if (!canUpload || uploading) return;
    if (
      selected.type !== 'text/csv' &&
      !selected.name.toLowerCase().endsWith('.csv')
    ) {
      setFileError('Only CSV files are allowed.');
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setFileError('File size must be less than 500KB.');
      return;
    }
    setFileError('');
    setFile(selected);
    uploadAndExtract(selected);
  };

  const removeFile = () => {
    setFile(null);
    onClear();
  };

  const resetFile = useCallback(() => {
    setFile(null);
    setFileError('');
  }, []);

  return {
    file,
    fileError,
    uploading,
    canUpload,
    templateLoading,
    downloadTemplate,
    selectFile,
    removeFile,
    resetFile,
  };
};

type ScheduleFileUploadProps = ReturnType<
  typeof useScheduleFileImport
>;

export function DownloadTemplateLink({
  templateLoading,
  downloadTemplate,
}: Pick<
  ScheduleFileUploadProps,
  'templateLoading' | 'downloadTemplate'
>) {
  return (
    <Link
      component="button"
      type="button"
      underline="none"
      onClick={downloadTemplate}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 14,
        color: TEAL,
        fontWeight: 500,
      }}>
      <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
      {templateLoading ? 'Downloading...' : 'Download Template'}
    </Link>
  );
}

export function ScheduleFileUpload({
  file,
  fileError,
  uploading,
  canUpload,
  selectFile,
  removeFile,
}: ScheduleFileUploadProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files.length > 0) {
      selectFile(event.dataTransfer.files[0]);
    }
  };

  const handleBrowse = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files[0]) {
      selectFile(event.target.files[0]);
    }
    event.target.value = '';
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      {!file ? (
        <Box
          onDrop={canUpload ? handleDrop : undefined}
          onDragOver={(e) => e.preventDefault()}
          sx={{
            borderRadius: 2,
            py: 4,
            px: 3,
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            cursor: canUpload ? 'pointer' : 'not-allowed',
            position: 'relative',
            opacity: canUpload ? 1 : 0.6,
          }}>
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            aria-label="Upload schedule file"
            title=""
            disabled={!canUpload}
            onChange={handleBrowse}
            style={{
              opacity: 0,
              position: 'absolute',
              inset: 0,
              cursor: canUpload ? 'pointer' : 'not-allowed',
              pointerEvents: canUpload ? 'auto' : 'none',
            }}
          />
          <CloudUploadOutlinedIcon
            sx={{ fontSize: 32, color: '#777', mb: 0.5 }}
          />
          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
            Drag & drop files or{' '}
            <Link
              component="button"
              type="button"
              underline="none"
              disabled={!canUpload}
              sx={{
                color: TEAL,
                fontWeight: 500,
                cursor: canUpload ? 'pointer' : 'not-allowed',
              }}
              onClick={() => {
                if (canUpload) inputRef.current?.click();
              }}>
              browse
            </Link>
          </Typography>
          <Typography variant="caption" sx={{ color: '#999' }}>
            Supported formats: CSV (max. size 500KB)
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: `1px solid ${uploading ? '#d0d5dd' : '#12B76A'}`,
            backgroundColor: uploading ? '#f9fafb' : '#F6FEF9',
            borderRadius: 2,
            px: 2,
            py: 1.25,
          }}>
          <Box display="flex" alignItems="center" gap={1}>
            {uploading ? (
              <CircularProgress size={18} sx={{ color: TEAL }} />
            ) : (
              <CheckCircleIcon sx={{ color: '#12B76A', fontSize: 20 }} />
            )}
            <Typography
              fontSize={14}
              color={uploading ? '#475467' : '#027A48'}>
              {file.name}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Remove file"
            disabled={uploading}
            onClick={removeFile}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {fileError && (
        <Box
          role="alert"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1.5,
            px: 1.5,
            py: 1.25,
            border: '1px solid #F04438',
            borderRadius: '4px',
            backgroundColor: '#FFF5F5',
          }}>
          <ErrorIcon sx={{ color: '#D92D20', fontSize: 18 }} />
          <Typography fontSize={13} color="#D92D20">
            File upload failed. {fileError}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
